import { parseDateKey, toDateKey } from './reportCalendarDateUtils'

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const FREQUENCY_LABELS = {
	month: 'Monthly',
	quarter: 'Quarterly',
	'bi-monthly': 'Bi-monthly',
	'bi-annual': 'Bi-annual',
	year: 'Yearly',
}

const normalizeStatusKey = (value) => String(value ?? '').trim().toLowerCase()

const hasChangedAtValue = (step) => (
	step != null
	&& typeof step === 'object'
	&& Object.prototype.hasOwnProperty.call(step, 'changed_at')
	&& step.changed_at != null
	&& String(step.changed_at).trim() !== ''
)

export const isReportCalendarItemSubmitted = (item) => {
	const progress = item?.progress
	if (normalizeStatusKey(progress?.current) === 'submitted') {
		return true
	}

	const steps = Array.isArray(progress?.steps) ? progress.steps : []
	return steps.some((step) => (
		normalizeStatusKey(step?.key) === 'submitted' && hasChangedAtValue(step)
	))
}

export const mapReportStatusToVariant = (status) => {
	const normalized = normalizeStatusKey(status)

	if (normalized === 'red') {
		return 'urgent'
	}

	if (normalized === 'yellow') {
		return 'yellow'
	}

	if (normalized === 'green' || normalized === 'success' || normalized === 'submitted') {
		return 'success'
	}

	return 'default'
}

export const mapReportItemToVariant = (item) => {
	if (isReportCalendarItemSubmitted(item)) {
		return 'success'
	}

	return mapReportStatusToVariant(item?.status)
}

export const buildReportItemId = (item) => (
	[
		item.company_id,
		item.date || item.exp_date,
		item.country_code,
		item.type_of_report || item.name,
		item.period,
		item.period_year,
		item.state_id,
		item.tax_number,
	].filter((part) => part !== null && part !== undefined && part !== '').join('-')
)

export const formatReportJurisdiction = (item) => {
	if (item.state) {
		return `${item.state} (${item.country || item.country_code || ''})`
	}

	return item.country || item.country_code || ''
}

export const formatReportFrequency = (periodType) => (
	FREQUENCY_LABELS[periodType] || periodType || '—'
)

export const formatReportPeriod = (item) => {
	const year = item.period_year
	const period = Number(item.period)

	if (item.period_type === 'month' && period >= 1 && period <= 12) {
		return `${MONTH_SHORT[period - 1]} ${year}`
	}

	if (item.period_type === 'quarter' && period) {
		return `Q${period} ${year}`
	}

	if (!period && !year) {
		return '—'
	}

	return [period, item.period_type, year].filter(Boolean).join(' ')
}

export const formatReportDueDateLabel = (dateKey) => {
	if (!dateKey) {
		return '—'
	}

	const date = parseDateKey(dateKey)
	return `${date.getDate()} ${MONTH_SHORT[date.getMonth()]}`
}

export const mapCalendarDatesToEvents = (dates = []) => (
	dates.flatMap((entry) => {
		const date = parseDateKey(entry.date)

		return (entry.statuses ?? []).map((status) => ({
			date,
			variant: mapReportStatusToVariant(status),
		}))
	})
)

export const mapTimelineDatesToEvents = (dates = []) => (
	dates.map((date) => ({
		date: parseDateKey(date),
	}))
)

export const mapTimelineItemsToEvents = (items = []) => (
	items.map((item) => ({
		date: parseDateKey(item.date || item.exp_date),
		variant: mapReportItemToVariant(item),
	}))
)

export const mergeCalendarPickerEvents = (calendarEvents = [], itemEvents = []) => {
	const itemDateKeys = new Set(
		itemEvents.map((event) => toDateKey(event.date)).filter(Boolean),
	)

	return [
		...calendarEvents.filter((event) => !itemDateKeys.has(toDateKey(event.date))),
		...itemEvents,
	]
}

const mapItemToTimelineTag = (item) => {
	const countryCode = item.country_code || ''
	const reportType = item.type_of_report || item.name || ''

	return {
		id: buildReportItemId(item),
		label: countryCode && reportType ? `${countryCode} · ${reportType}` : reportType || countryCode,
		variant: mapReportItemToVariant(item),
		source: item,
	}
}

const getItemDateKey = (item) => item.date || item.exp_date

export const buildTimelineCompaniesFromApi = (dates, businesses = [], items = []) => {
	const companies = businesses.length > 0
		? businesses
		: Array.from(
			new Map(
				items.map((item) => [item.company_id, {
					id: item.company_id,
					name: item.company_name,
				}]),
			).values(),
		)

	return companies.map((company) => ({
		name: company.name,
		cells: dates.map((date) => {
			const dateKey = toDateKey(date)

			return items
				.filter((item) => (
					item.company_id === company.id
					&& getItemDateKey(item) === dateKey
				))
				.map(mapItemToTimelineTag)
		}),
	}))
}

export const buildUpcomingReportsGroupsFromApi = (months = []) => (
	months.map((month) => ({
		monthKey: month.key,
		monthLabel: month.label,
		items: (month.items ?? []).map((item) => {
			const dateKey = getItemDateKey(item)
			const date = parseDateKey(dateKey)

			return {
				id: buildReportItemId(item),
				dateKey,
				weekday: WEEKDAY_SHORT[date.getDay()],
				day: date.getDate(),
				monthLabel: month.label,
				jurisdiction: formatReportJurisdiction(item),
				reportType: item.type_of_report || item.name || '',
				company: item.company_name,
				variant: mapReportItemToVariant(item),
				source: item,
			}
		}),
	}))
)

const FILTER_LIST_KEYS = {
	businesses: ['businesses', 'companies', 'company', 'business'],
	jurisdictions: ['jurisdictions', 'jurisdiction', 'countries', 'country'],
	reportTypes: ['report_types', 'reportTypes', 'types', 'type_of_report'],
	statuses: ['statuses', 'status'],
}

const FILTER_VALUE_KEYS = {
	businesses: ['id', 'company_id', 'value', 'code'],
	jurisdictions: ['code', 'country_code', 'value', 'id'],
	reportTypes: ['value', 'code', 'type_of_report', 'id', 'name'],
	statuses: ['value', 'code', 'status', 'id'],
}

const FILTER_LABEL_KEYS = {
	businesses: ['name', 'company_name', 'label', 'title'],
	jurisdictions: ['name', 'country', 'label', 'title'],
	reportTypes: ['label', 'name', 'title', 'type_of_report'],
	statuses: ['label', 'name', 'title'],
}

const STATUS_FALLBACK_LABELS = {
	red: 'Overdue',
	yellow: 'Due soon',
	'not come date': 'Upcoming',
	not_come_date: 'Upcoming',
}

const pickFirstArray = (payload, keys) => {
	for (const key of keys) {
		if (Array.isArray(payload?.[key])) {
			return payload[key]
		}
	}

	return []
}

const pickFirstValue = (item, keys) => {
	for (const key of keys) {
		if (item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== '') {
			return item[key]
		}
	}

	return undefined
}

const mapFilterOption = (item, valueKeys, labelKeys, fallbackLabels = {}) => {
	if (item == null) {
		return null
	}

	if (typeof item === 'string' || typeof item === 'number') {
		const value = String(item)
		return {
			value,
			label: fallbackLabels[value.toLowerCase()] || value,
		}
	}

	const value = pickFirstValue(item, valueKeys)
	const label = pickFirstValue(item, labelKeys)

	if (value == null) {
		return null
	}

	const normalized = String(value)

	return {
		value: normalized,
		label: String(label || fallbackLabels[normalized.toLowerCase()] || normalized),
	}
}

export const REPORT_CALENDAR_FILTER_ALL = 'all'

export const REPORT_CALENDAR_INITIAL_FILTER_VALUES = {
	business: REPORT_CALENDAR_FILTER_ALL,
	jurisdiction: REPORT_CALENDAR_FILTER_ALL,
	reportType: REPORT_CALENDAR_FILTER_ALL,
	status: REPORT_CALENDAR_FILTER_ALL,
}

export const normalizeReportCalendarFilters = (payload) => {
	const source = payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
		? payload.data
		: payload ?? {}

	const toOptions = (group, fallbackLabels) => (
		pickFirstArray(source, FILTER_LIST_KEYS[group])
			.map((item) => mapFilterOption(
				item,
				FILTER_VALUE_KEYS[group],
				FILTER_LABEL_KEYS[group],
				fallbackLabels,
			))
			.filter(Boolean)
	)

	return {
		businesses: toOptions('businesses'),
		jurisdictions: toOptions('jurisdictions'),
		reportTypes: toOptions('reportTypes'),
		statuses: toOptions('statuses', STATUS_FALLBACK_LABELS),
	}
}

export const buildReportCalendarFilterQuery = (filterValues = {}) => {
	const query = {}

	if (filterValues.business && filterValues.business !== REPORT_CALENDAR_FILTER_ALL) {
		query.company_id = filterValues.business
	}

	if (filterValues.jurisdiction && filterValues.jurisdiction !== REPORT_CALENDAR_FILTER_ALL) {
		query.jurisdiction = filterValues.jurisdiction
	}

	if (filterValues.reportType && filterValues.reportType !== REPORT_CALENDAR_FILTER_ALL) {
		query.report_type = filterValues.reportType
	}

	if (filterValues.status && filterValues.status !== REPORT_CALENDAR_FILTER_ALL) {
		query.status = filterValues.status
	}

	return query
}
